/**
 * Note: this is the carbonized version of the  NewConditionForm in `underwriter_dashboard/shared/Conditions`
 */
import React, { useState, useEffect } from "react";
import { startCase } from "lodash";
import { Formik } from "formik";
import {
  Button,
  ComboBox,
  Form,
  FormGroup,
  InlineLoading,
  Link,
  Select,
  SelectItem,
  SelectSkeleton,
  TextArea,
} from "carbon-components-react";

import { docTypeToHumanStr, selectableDocTypes } from "underwriter_dashboard/shared";
import {
  createCondition,
  getConditionsTemplate,
  getConditionsTemplates,
} from "underwriter_dashboard/shared/Conditions/APIHelpers";
import { ConditionTemplateListItem } from "../../../../../shared/types/api";

const styles = require("./styles.module.scss");

const ownerOptions = [
  { label: "Borrower", value: "borrower" },
  { label: "Closing Agent", value: "closing_agent" },
  { label: "Lender", value: "lender" },
];

const statusOptions = [
  { label: "Open", value: "open" },
  { label: "Requested", value: "requested" },
];

const stageOptions = [
  { label: "Pre Qualification", value: "pre_qualification" },
  { label: "Pre Decision", value: "pre_decision" },
  { label: "Pre Closing", value: "pre_closing" },
  { label: "At Closing", value: "at_closing" },
  { label: "Post Closing", value: "post_closing" },
];

const docTypeSelectOpts: any = selectableDocTypes.map(t => ({
  value: t,
  label: docTypeToHumanStr(t),
}));

interface Props {
  appId: number;

  notifySuccess: Function;
  notifyError: Function;
}

const ConditionsPanel: React.FC<Props> = ({ appId, notifySuccess, notifyError }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [templates, setTemplates] = useState(null);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>("");
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>("");

  const [comboBoxTextSearch, setComboBoxTextSearch] = useState("");

  useEffect(() => {
    getConditionsTemplates().then(resp => setTemplates(resp.templates));
  }, []);

  const initialValues = {
    status: "open",
  } as any;

  return (
    <>
      <div className={styles.panelContent}>
        <Formik
          enableReinitialize
          onSubmit={(values, { resetForm }) => {
            const { template_text, owner, stage, expected_document_type, status } = values;
            createCondition(
              template_text,
              owner,
              stage,
              appId,
              expected_document_type,
              status,
              selectedTemplateType,
            )
              .then(() => {
                setLoading(false);
                notifySuccess("Condition saved successfully");
                resetForm();

                // Clear the category, the condition type, and the search query
                setSelectedTemplateCategory("");
                setSelectedTemplateType("");
                setComboBoxTextSearch("");
              })
              .catch(() => notifyError("Couldn't save condition"));
          }}
          initialValues={initialValues}
        >
          {({ values, errors, touched, handleChange, handleBlur, setValues, handleSubmit }) => {
            const { template_text, stage, owner, expected_document_type, status } = values;

            const templateTypes =
              templates && selectedTemplateCategory ? templates[selectedTemplateCategory] : [];
            const types = templateTypes.sort(
              (a: ConditionTemplateListItem, b: ConditionTemplateListItem) => {
                // Names should be unique and never the same, skip equality comparison
                return a.description < b.description ? -1 : 1;
              },
            );

            const placeholderPatterns = [
              new RegExp(/\[\[[A-Za-z ?0-9]*\]\]/g), // [[abc 1]]
              new RegExp(/x,?xxx/g), // x,xxx or xxxx
            ];

            const submitDisabled = placeholderPatterns.some(pattern => pattern.test(template_text));
            const disabled = selectedTemplateType === "";

            return (
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  setLoading(true);
                  handleSubmit(values);
                  setComboBoxTextSearch("");
                }}
              >
                {templates ? (
                  <>
                    <Select
                      labelText="Category"
                      id="category"
                      name="category"
                      value={selectedTemplateCategory || ""}
                      onChange={e => setSelectedTemplateCategory(e.target.value)}
                      size="sm"
                    >
                      <SelectItem value="" text="Choose a category..." />
                      {templates &&
                        Object.keys(templates).map((t: string) => (
                          <SelectItem key={t} value={t} text={startCase(t)} />
                        ))}
                    </Select>
                    {selectedTemplateCategory !== "" && (
                      <ComboBox
                        id="condition-type"
                        name="type"
                        titleText="Condition Type *"
                        placeholder="Condition Type"
                        items={types}
                        itemToString={e => (e ? e.description : "")}
                        shouldFilterItem={itemOption => {
                          if (comboBoxTextSearch === "") {
                            return true;
                          }

                          const { item } = itemOption;
                          const { name, description } = item as ConditionTemplateListItem;
                          return [name, description]
                            .map(s => s.toLowerCase())
                            .some(s => s.includes(comboBoxTextSearch));
                        }}
                        onChange={e => {
                          const { selectedItem } = e;
                          if (!selectedItem) return;

                          const { name } = selectedItem;
                          setSelectedTemplateType(name);
                          getConditionsTemplate(name).then(resp => setValues(resp.template));
                        }}
                        disabled={selectedTemplateType !== ""}
                        onInputChange={searchQuery => {
                          if (!searchQuery) return;
                          setComboBoxTextSearch(searchQuery.toLowerCase());
                        }}
                        light
                        size="sm"
                        className={styles.typeSelector}
                      />
                    )}

                    {selectedTemplateType !== "" && (
                      <Button
                        onClick={() => {
                          setComboBoxTextSearch("");
                          setSelectedTemplateType("");
                        }}
                        size="sm"
                        kind="ghost"
                      >
                        Select new type
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <SelectSkeleton />
                    <SelectSkeleton />
                  </>
                )}

                <hr />
                {selectedTemplateCategory !== "" && selectedTemplateType !== "" && (
                  <>
                    <FormGroup legendText="Add a Condition">
                      <TextArea
                        id="condition"
                        name="template_text"
                        value={template_text || ""}
                        labelText=""
                        placeholder="Please provide so-and-so document..."
                        invalidText={
                          template_text === ""
                            ? "A valid condition description is required"
                            : "Replace any placeholder text (e.g. [[xxxx]] )"
                        }
                        invalid={
                          (!!errors.template_text && touched.template_text) || submitDisabled
                        }
                        onChange={handleChange}
                        disabled={disabled}
                      />
                      <Select
                        labelText="Stage"
                        id="stage"
                        name="stage"
                        value={stage || ""}
                        onChange={handleChange}
                        invalid={!!errors.stage && touched.stage}
                        invalidText="Stage is required"
                        disabled={disabled}
                        size="sm"
                      >
                        <SelectItem value="" text="Choose a stage…" />
                        {stageOptions.map(({ label, value }) => (
                          <SelectItem text={label} value={value} key={value} />
                        ))}
                      </Select>
                      <Select
                        labelText="Owner"
                        id="owner"
                        name="owner"
                        value={owner || ""}
                        onChange={handleChange}
                        invalid={!!errors.owner && touched.owner}
                        invalidText="Owner is required"
                        disabled={disabled}
                        size="sm"
                      >
                        <SelectItem value="" text="Choose an owner…" />
                        {ownerOptions.map(({ label, value }) => (
                          <SelectItem text={label} value={value} key={value} />
                        ))}
                      </Select>
                      <Select
                        labelText="Expected document type"
                        id="expected_document_type"
                        name="expected_document_type"
                        value={expected_document_type || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!errors.expected_document_type && touched.expected_document_type}
                        disabled={disabled}
                        size="sm"
                      >
                        {docTypeSelectOpts.map(({ label, value }) => (
                          <SelectItem text={label} value={value} key={value} />
                        ))}
                      </Select>
                      <Select
                        labelText="Status"
                        id="status"
                        name="status"
                        value={status || ""}
                        onChange={handleChange}
                        invalid={!!errors.status && touched.status}
                        disabled={disabled}
                        size="sm"
                      >
                        {statusOptions.map(({ label, value }) => (
                          <SelectItem text={label} value={value} key={value} />
                        ))}
                      </Select>
                    </FormGroup>
                    <div className={styles.buttons}>
                      {loading ? (
                        <InlineLoading
                          description="Saving condition..."
                          status="active"
                          className={styles.loading}
                        />
                      ) : (
                        <>
                          <Button
                            kind="secondary"
                            tabIndex={0}
                            onClick={() => setValues(initialValues)}
                          >
                            Cancel
                          </Button>
                          <Button
                            kind="primary"
                            type="submit"
                            tabIndex={0}
                            disabled={submitDisabled}
                          >
                            Save
                          </Button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default ConditionsPanel;
